import { Injectable } from '@angular/core';
import { Message } from 'primeng/api';
import { roundCurrency } from '../utils/payment.util';

/**
 * PaymentValidationService
 *
 * Stateless validation/threshold-check logic for the Payment Total Dialog.
 * Extracted from DashboardComponent's validate*Amount methods. Each method
 * is a pure function: it does NOT call messageService.add() or mutate
 * anything itself — it returns a result describing what (if anything)
 * should change and what toast (if any) the caller should show. This keeps
 * the service dependency-free and root-providable (DashboardComponent
 * already owns a MessageService instance from its own `providers` array —
 * having this service depend on MessageService directly would require it
 * to be scoped under a component that provides one, which DashboardComponent
 * already is, but keeping it dependency-free avoids that coupling entirely).
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentValidationService {
  roundCurrency(value: number): number {
    return roundCurrency(value);
  }

  /** Mirrors DashboardComponent.validatePartPaymentAmount()'s decision logic. */
  shouldDisablePartPayment(
    currentAmount: number,
    totalAmount: number,
    isPartPaymentEnabled: boolean,
  ): boolean {
    return isPartPaymentEnabled && currentAmount >= totalAmount;
  }

  partPaymentDisabledMessage(): Message {
    return {
      severity: 'info',
      summary: 'Full Payment',
      detail: 'Amount equals total. Treating as normal payment.',
      life: 2000,
    };
  }

  /** validateCashAmount()'s exchange-order auto-calculated refund. */
  computeExchangeRefundCash(roundedTotal: number, cashAmount: number): number {
    return roundedTotal + (cashAmount || 0);
  }

  /** validateCashAmount()'s regular-order auto-calculated refund. */
  computeRegularRefundCash(balance: number): number {
    return balance < 0 ? Math.abs(balance) : 0;
  }

  /** validateRefundCashAmount()'s max-allowed-refund computation. */
  getMaxRefundCash(
    isExchangeOrder: boolean,
    roundedTotal: number,
    isCashSelected: boolean,
    cashAmount: number | null,
    absoluteBalance: number,
  ): number {
    if (isExchangeOrder) {
      let exchangeBalance = roundedTotal; // Exchange total (return items)
      if (isCashSelected && cashAmount !== null) {
        exchangeBalance += cashAmount;
      }
      return Math.abs(exchangeBalance);
    }
    return absoluteBalance;
  }

  /** Returns a warning message if refundCashAmount exceeds maxRefund, else null. */
  validateRefundCashAmount(refundCashAmount: number, maxRefund: number): Message | null {
    if (refundCashAmount > maxRefund) {
      return {
        severity: 'warn',
        summary: 'Invalid Refund Amount',
        detail: `Refund amount (₹${refundCashAmount}) cannot exceed balance (₹${maxRefund})`,
        life: 3000,
      };
    }
    return null;
  }

  /** Returns a warning message if redeemCreditAmount is invalid, else null.
   * (Matches the original: it warns but does NOT auto-correct the value —
   * the reset lines were already commented out in the source.) */
  validateRedeemCreditAmount(
    redeemCreditAmount: number,
    availableCreditAmount: number,
    roundedTotal: number,
  ): Message | null {
    if (redeemCreditAmount > availableCreditAmount) {
      return {
        severity: 'warn',
        summary: 'Invalid Amount',
        detail: `Redeem amount cannot exceed available credit (₹${availableCreditAmount})`,
        life: 3000,
      };
    }

    if (redeemCreditAmount > roundedTotal) {
      return {
        severity: 'warn',
        summary: 'Amount Too High',
        detail: `Redeem amount (₹${redeemCreditAmount}) cannot exceed order total (₹${roundedTotal}) to avoid refund`,
        life: 3000,
      };
    }

    return null;
  }

  /** Blocking validation used before placing an order. */
  isRedeemCreditValid(
    isRedeemCreditSelected: boolean,
    redeemCreditAmount: number,
    availableCreditAmount: number,
    roundedTotal: number,
  ): boolean {
    if (!isRedeemCreditSelected || redeemCreditAmount <= 0) {
      return true; // No validation needed if not using redeem credit
    }

    if (redeemCreditAmount > availableCreditAmount) {
      return false;
    }

    if (redeemCreditAmount > roundedTotal) {
      return false;
    }

    return true;
  }
}
